import theme from 'ThemeProvider/theme';
import sx from 'utils/createSx';

export default sx({
  drawer: {
    zIndex: theme.zIndex.drawer + 1,
  },
  drawerPaper: {
    backgroundColor: theme.palette.background.paper,
    borderLeft: `1px solid ${theme.palette.divider}`,
  },

  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },

  // Header
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  title: {
    fontWeight: 'bold',
    fontSize: '1.1rem',
    letterSpacing: '0.5px',
  },
  closeButton: {
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },

  // Content
  content: {
    flex: 1,
    overflow: 'auto',
    padding: theme.spacing(1),
  },

  // Empty Cart
  emptyCart: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '60%',
    textAlign: 'center',
    padding: theme.spacing(3),
  },
  emptyIcon: {
    fontSize: '3rem',
    color: theme.palette.grey[400],
    marginBottom: theme.spacing(2),
  },
  emptyTitle: {
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  emptySubtitle: {
    maxWidth: '250px',
  },

  // Items List
  itemsList: {
    padding: 0,
  },
  cartItem: {
    flexDirection: 'column',
    alignItems: 'stretch',
    padding: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },

  // Item Container
  itemContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  },

  // Product Image
  imageContainer: {
    width: 60,
    height: 60,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[100],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  },
  productImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },

  // Product Info
  productInfo: {
    flex: 1,
    minWidth: 0,
  },
  productName: {
    fontWeight: 600,
    fontSize: '0.875rem',
    lineHeight: 1.2,
    marginBottom: theme.spacing(0.5),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  productBrewery: {
    fontSize: '0.75rem',
    marginBottom: theme.spacing(0.5),
  },
  productPrice: {
    fontWeight: 600,
    color: theme.palette.primary.main,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(0.25),
  },
  subscriberPrice: {
    fontSize: '0.7rem',
    color: theme.palette.success.main,
    fontWeight: 500,
  },

  // Quantity Controls
  quantityControls: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minWidth: 40,
  },
  quantityButton: {
    padding: theme.spacing(0.25),
    backgroundColor: theme.palette.grey[100],
    border: `1px solid ${theme.palette.grey[300]}`,
    '&:hover': {
      backgroundColor: theme.palette.grey[200],
    },
    '&:disabled': {
      backgroundColor: theme.palette.grey[50],
      color: theme.palette.grey[400],
    },
  },
  quantity: {
    fontWeight: 600,
    fontSize: '0.875rem',
    minWidth: 20,
    textAlign: 'center',
  },

  // Remove Button
  removeButton: {
    padding: theme.spacing(0.5),
    color: theme.palette.error.main,
    '&:hover': {
      backgroundColor: theme.palette.error.light + '20',
    },
  },

  // Item Total
  itemTotal: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: theme.spacing(0.5),
    borderTop: `1px solid ${theme.palette.grey[200]}`,
  },
  itemTotalText: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  itemSubscriberTotal: {
    color: theme.palette.success.main,
    fontWeight: 500,
  },

  // Summary
  summary: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    margin: theme.spacing(1, 0),
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  summaryValue: {
    fontWeight: 500,
  },
  totalLabel: {
    fontWeight: 'bold',
    fontSize: '1.1rem',
  },
  totalValue: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  subscriberTotalLabel: {
    color: theme.palette.success.main,
    fontWeight: 600,
  },
  subscriberTotalValue: {
    color: theme.palette.success.main,
    fontWeight: 'bold',
  },
  savingsContainer: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.success.light + '20',
    borderRadius: theme.shape.borderRadius,
    textAlign: 'center',
  },
  savings: {
    color: theme.palette.success.dark,
    fontWeight: 600,
  },
  cashbackBanner: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    textAlign: 'center',
  },
  cashbackBannerLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    opacity: 0.9,
    marginBottom: theme.spacing(0.5),
  },
  cashbackBannerAmount: {
    fontSize: '1.35rem',
    fontWeight: 800,
    lineHeight: 1.2,
  },
  cashbackBannerHint: {
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.5),
    opacity: 0.95,
  },
  cashbackBalanceBanner: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.25),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.primary.main}`,
    backgroundColor: 'rgba(201, 108, 30, 0.08)',
    textAlign: 'center',
  },
  cashbackBalanceLabel: {
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(0.25),
  },
  cashbackBalanceAmount: {
    fontSize: '1.1rem',
    fontWeight: 700,
    color: theme.palette.primary.main,
  },

  // Footer
  footer: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  persistenceIndicator: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(0.5),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  clearButton: {
    borderColor: theme.palette.grey[400],
    color: theme.palette.grey[600],
    '&:hover': {
      borderColor: theme.palette.error.main,
      color: theme.palette.error.main,
      backgroundColor: theme.palette.error.light + '10',
    },
  },
  checkoutButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 'bold',
    padding: theme.spacing(1.5),
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
});
